import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  Videocam as VideoCallIcon,
  PlayArrow as LaunchIcon,
  CheckCircle as DoneIcon,
  School as CourseIcon,
  ChevronRight as ChevronRightIcon,
  MenuBook as OutlineIcon,
  Layers as VersionIcon,
  OpenInNew as RejoinIcon,
  Quiz as ExamIcon,
  FreeBreakfast as BreakIcon,
  LibraryBooks as MaterialIcon,
  Topic as PlaceholderIcon,
  ArrowBack as BackIcon,
  VideoLibrary as RecordingsIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
  Block as DisabledIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import {
  defaultAvailableUntilDateInput,
  formatDateTime,
  formatHms,
  visibilityLabel,
} from './recordingDisplayUtils';

const TYPE_META = {
  live_video_call: { label: 'Join Video Call', icon: VideoCallIcon, color: 'secondary' },
  material: { label: 'Material', icon: MaterialIcon, color: 'default' },
  exam: { label: 'Exam', icon: ExamIcon, color: 'warning' },
  break: { label: 'Break', icon: BreakIcon, color: 'default' },
  placeholder: { label: 'Placeholder', icon: PlaceholderIcon, color: 'default' },
};

function outlineItemTitle(item) {
  if (item.item_type === 'live_video_call') return item.label || 'Join Video Call';
  if (item.item_type === 'material') {
    return item.material?.title || item.label || 'Material';
  }
  if (item.item_type === 'exam') return item.exam?.name || item.label || 'Exam';
  if (item.item_type === 'break') return item.label || 'Break';
  if (item.item_type === 'placeholder') return item.label || 'Placeholder';
  return item.label || item.item_type || 'Item';
}

function outlineItemSecondary(item) {
  if (item.item_type === 'live_video_call') {
    return item.notes || 'Launch Meet for this outline step — learners join from My Training';
  }
  if (item.item_type === 'material') {
    const code = item.material?.material_code ? ` · ${item.material.material_code}` : '';
    const ct = item.material?.content_type || item.content_type || '';
    return `${ct || 'Material'}${code}`;
  }
  if (item.item_type === 'exam') return 'Exam / assessment';
  if (item.notes) return item.notes;
  return item.schedule_label || null;
}

function formatBytes(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

const ROW_PALETTE = [
  { bg: '#E8F4FC', border: '#4A90C8', accent: '#0F4C75' },
  { bg: '#EEF8F1', border: '#3D9B6A', accent: '#1B5E3B' },
  { bg: '#FFF4E5', border: '#E6A023', accent: '#8A5A00' },
  { bg: '#E6F7F5', border: '#2A9B91', accent: '#0D5C56' },
  { bg: '#FFE8EE', border: '#D45B7A', accent: '#8B2040' },
  { bg: '#F0EDE6', border: '#8A7A5C', accent: '#4A3F2A' },
];

function rowPaletteFor(key) {
  const s = String(key || 'x');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ROW_PALETTE[h % ROW_PALETTE.length];
}

/**
 * Instructor Dashboard — Course name → versions → Outline | Recordings.
 */
export default function TrainingInstructorDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [editionTab, setEditionTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    courseGroups: [],
    finishedCourseGroups: [],
    liveSessions: [],
    today: '',
  });
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedEditionId, setSelectedEditionId] = useState(null);
  const [launchingKey, setLaunchingKey] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [playRecording, setPlayRecording] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [visibilityDialog, setVisibilityDialog] = useState(null);
  const [visibilityForm, setVisibilityForm] = useState({
    visibility: 'private',
    available_until: defaultAvailableUntilDateInput(30),
  });
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [allRecordings, setAllRecordings] = useState([]);
  const [allRecordingsLoading, setAllRecordingsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/instructor/dashboard');
      setData(
        r.data?.data || {
          courseGroups: [],
          finishedCourseGroups: [],
          liveSessions: [],
          today: '',
        }
      );
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load instructor dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = tab === 0 ? data.courseGroups || [] : data.finishedCourseGroups || [];
  const live = (data.liveSessions || []).filter((s) => s.status === 'live');

  const selectedGroup = useMemo(
    () => groups.find((g) => g.groupId === selectedGroupId) || null,
    [groups, selectedGroupId]
  );
  const selectedEdition = useMemo(
    () => (selectedGroup?.editions || []).find((e) => e.id === selectedEditionId) || null,
    [selectedGroup, selectedEditionId]
  );

  useEffect(() => {
    setSelectedGroupId(null);
    setSelectedEditionId(null);
    setEditionTab(0);
  }, [tab]);

  const loadRecordings = useCallback(async (titleId) => {
    if (!titleId) return;
    setRecordingsLoading(true);
    try {
      const r = await api.get(`/training/training-titles/${titleId}/live-recordings`);
      setRecordings(r.data?.data?.recordings || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load recordings');
      setRecordings([]);
    } finally {
      setRecordingsLoading(false);
    }
  }, []);

  const loadAllRecordings = useCallback(async () => {
    setAllRecordingsLoading(true);
    try {
      const r = await api.get('/training/instructor/live-recordings');
      setAllRecordings(r.data?.data?.recordings || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load all recordings');
      setAllRecordings([]);
    } finally {
      setAllRecordingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllRecordings();
  }, [loadAllRecordings]);

  const openVisibilityDialog = (rec) => {
    const until =
      rec.available_until != null
        ? String(rec.available_until).slice(0, 10)
        : defaultAvailableUntilDateInput(30);
    setVisibilityForm({
      visibility: rec.visibility || 'private',
      available_until: until,
    });
    setVisibilityDialog(rec);
  };

  const saveVisibility = async () => {
    if (!visibilityDialog?.id) return;
    setSavingVisibility(true);
    try {
      const body = {
        visibility: visibilityForm.visibility,
      };
      if (visibilityForm.visibility === 'public' || visibilityForm.visibility === 'private') {
        body.available_until = visibilityForm.available_until
          ? `${visibilityForm.available_until}T23:59:59.000Z`
          : defaultAvailableUntilDateInput(30);
      }
      await api.put(`/training/live-recordings/${visibilityDialog.id}`, body);
      toast.success('Recording visibility updated');
      setVisibilityDialog(null);
      if (selectedEditionId) await loadRecordings(selectedEditionId);
      if (tab === 2) await loadAllRecordings();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  const markForDeletion = async (rec) => {
    try {
      await api.put(`/training/live-recordings/${rec.id}`, { mark_for_deletion: true });
      toast.success('Marked for deletion');
      if (selectedEditionId) await loadRecordings(selectedEditionId);
      if (tab === 2) await loadAllRecordings();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to mark for deletion');
    }
  };

  const clearDeletionMark = async (rec) => {
    try {
      await api.put(`/training/live-recordings/${rec.id}`, { clear_deletion_mark: true });
      toast.success('Deletion mark cleared');
      if (selectedEditionId) await loadRecordings(selectedEditionId);
      if (tab === 2) await loadAllRecordings();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to clear deletion mark');
    }
  };

  useEffect(() => {
    setEditionTab(0);
    setRecordings([]);
    if (selectedEditionId) {
      loadRecordings(selectedEditionId);
    }
  }, [selectedEditionId, loadRecordings]);

  useEffect(() => {
    if (selectedEdition?.id && editionTab === 1) {
      loadRecordings(selectedEdition.id);
    }
    // Refresh list when switching to the Recordings tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionTab]);

  useEffect(() => {
    if (tab === 2) {
      loadAllRecordings();
    }
  }, [tab, loadAllRecordings]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    const open = async () => {
      if (!playRecording?.play_path) {
        setPlayUrl(null);
        return;
      }
      try {
        const path = playRecording.play_path.startsWith('/training/')
          ? playRecording.play_path
          : `/training/live-recordings/${playRecording.id}/stream`;
        const res = await api.get(path, { responseType: 'blob' });
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setPlayUrl(objectUrl);
      } catch (e) {
        if (!active) return;
        toast.error(e.response?.data?.message || 'Could not play recording');
        setPlayRecording(null);
        setPlayUrl(null);
      }
    };
    open();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [playRecording]);

  const recordingsBySequence = useMemo(() => {
    const map = new Map();
    for (const rec of recordings) {
      const key = rec.recording_label || String(rec.launch_sequence || 1).padStart(4, '0');
      if (!map.has(key)) {
        map.set(key, {
          label: key,
          launch_sequence: rec.launch_sequence || 1,
          items: [],
        });
      }
      map.get(key).items.push(rec);
    }
    return [...map.values()].sort((a, b) => a.launch_sequence - b.launch_sequence);
  }, [recordings]);

  const liveForEditionItem = (editionId, contentItemId) =>
    live.find(
      (s) =>
        String(s.training_title_id) === String(editionId) &&
        (!contentItemId || String(s.content_item_id) === String(contentItemId))
    );

  const launchItem = async (edition, item) => {
    if (!edition || !item || item.item_type !== 'live_video_call') return;
    if (edition.status === 'admin_blocked') {
      toast.error('This course edition is admin-blocked');
      return;
    }
    const existing = liveForEditionItem(edition.id, item.id);
    if (existing?.id) {
      navigate(`/training/live/${existing.id}`);
      return;
    }
    const key = `${edition.id}:${item.id}`;
    setLaunchingKey(key);
    try {
      const r = await api.post('/training/live-sessions/launch', {
        training_title_id: edition.id,
        content_item_id: item.id,
        session_date: data.today || undefined,
      });
      const session = r.data?.data;
      toast.success(r.data?.message || 'Session launched');
      if (session?.id) {
        navigate(`/training/live/${session.id}`);
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not launch session');
    } finally {
      setLaunchingKey(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const launchExample = selectedEdition?.course_code
    ? `${selectedEdition.course_code}-${selectedEdition.edition_label}-0001`
    : null;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Instructor Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open a course, pick a version, then deliver from the outline or review recordings. Launch{' '}
        <strong>Join Video Call</strong> so learners can join from My Training.
        {data.today ? ` Today: ${data.today}` : ''}
      </Typography>

      {live.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<VideoCallIcon />}>
          {live.length} live session{live.length === 1 ? '' : 's'} in progress.{' '}
          {live.map((s) => (
            <Button
              key={s.id}
              size="small"
              sx={{ ml: 1 }}
              startIcon={<RejoinIcon />}
              onClick={() => navigate(`/training/live/${s.id}`)}
            >
              {s.meeting_subject || s.title?.name || 'Rejoin'}
            </Button>
          ))}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab
          icon={<CourseIcon />}
          iconPosition="start"
          label={`Courses (${(data.courseGroups || []).length})`}
        />
        <Tab
          icon={<DoneIcon />}
          iconPosition="start"
          label={`Finished (${(data.finishedCourseGroups || []).length})`}
        />
        <Tab
          icon={<RecordingsIcon />}
          iconPosition="start"
          label={`All Record Videos (${allRecordingsLoading ? '…' : allRecordings.length})`}
        />
      </Tabs>

      {tab === 2 ? (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  All Record Videos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Every cloud recording across your courses — view, set public/private/disabled, or
                  mark for deletion.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={loadAllRecordings}
                disabled={allRecordingsLoading}
              >
                Refresh
              </Button>
            </Stack>

            {allRecordingsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : allRecordings.length === 0 ? (
              <Alert severity="info">No recordings yet for your instructor courses.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, minWidth: 280 }}>
                        Category → Subcategory → Course → Version
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allRecordings.map((rec) => {
                      const pathKey = rec.course_path || rec.training_title_id;
                      const palette = rowPaletteFor(pathKey);
                      const isDeleted = Boolean(rec.marked_for_deletion_at);
                      return (
                        <TableRow
                          key={rec.id}
                          hover
                          sx={{
                            bgcolor: palette.bg,
                            borderLeft: `4px solid ${palette.border}`,
                            opacity: isDeleted ? 0.7 : 1,
                            '& td': { borderBottomColor: `${palette.border}55` },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: palette.accent, lineHeight: 1.35 }}
                            >
                              {rec.course_path ||
                                [
                                  rec.category?.name,
                                  rec.subCategory?.name,
                                  rec.title?.name,
                                  rec.edition_label,
                                ]
                                  .filter(Boolean)
                                  .join(' → ') ||
                                '—'}
                            </Typography>
                            {rec.title?.course_code && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'ui-monospace, monospace' }}
                              >
                                {rec.title.course_code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}
                            >
                              {rec.recording_label}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {formatDateTime(rec.recording_started_at || rec.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              {formatDateTime(rec.recording_ended_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={formatHms(rec.duration_seconds)} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              <Chip
                                size="small"
                                icon={
                                  rec.visibility === 'public' ? (
                                    <PublicIcon />
                                  ) : rec.visibility === 'disabled' ? (
                                    <DisabledIcon />
                                  ) : (
                                    <PrivateIcon />
                                  )
                                }
                                label={visibilityLabel(rec.visibility)}
                                color={
                                  rec.visibility === 'public'
                                    ? 'success'
                                    : rec.visibility === 'disabled'
                                      ? 'default'
                                      : 'primary'
                                }
                                variant="outlined"
                              />
                              {isDeleted && (
                                <Chip size="small" color="error" label="Delete marked" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<LaunchIcon />}
                                onClick={() => setPlayRecording(rec)}
                                sx={{ bgcolor: palette.accent }}
                              >
                                View
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => openVisibilityDialog(rec)}>
                                Public / Private
                              </Button>
                              {isDeleted ? (
                                <Button size="small" onClick={() => clearDeletionMark(rec)}>
                                  Undo delete
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => markForDeletion(rec)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
      <Breadcrumbs sx={{ mb: 2 }} separator={<ChevronRightIcon fontSize="small" />}>
        <Link
          component="button"
          type="button"
          underline="hover"
          color={!selectedGroupId ? 'text.primary' : 'inherit'}
          onClick={() => {
            setSelectedGroupId(null);
            setSelectedEditionId(null);
          }}
          sx={{
            fontWeight: !selectedGroupId ? 700 : 400,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            p: 0,
            font: 'inherit',
          }}
        >
          Courses
        </Link>
        {selectedGroup && (
          <Link
            component="button"
            type="button"
            underline="hover"
            color={!selectedEditionId ? 'text.primary' : 'inherit'}
            onClick={() => setSelectedEditionId(null)}
            sx={{
              fontWeight: !selectedEditionId ? 700 : 400,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              p: 0,
              font: 'inherit',
            }}
          >
            {selectedGroup.name}
          </Link>
        )}
        {selectedEdition && (
          <Typography color="text.primary" fontWeight={700}>
            {selectedEdition.edition_label}
            {selectedEdition.course_code ? ` · ${selectedEdition.course_code}` : ''}
          </Typography>
        )}
      </Breadcrumbs>

      {!selectedGroup && (
        <Card variant="outlined">
          <CardContent sx={{ p: groups.length ? 1 : 3, '&:last-child': { pb: groups.length ? 1 : 3 } }}>
            {groups.length === 0 ? (
              <Typography color="text.secondary">
                {tab === 0
                  ? 'You are not assigned as instructor on any courses yet. Ask a training admin to add you on the course.'
                  : 'No finished courses yet.'}
              </Typography>
            ) : (
              <List disablePadding>
                {groups.map((g) => (
                  <ListItem key={g.groupId} disablePadding divider>
                    <ListItemButton
                      onClick={() => {
                        setSelectedGroupId(g.groupId);
                        setSelectedEditionId(null);
                      }}
                    >
                      <ListItemIcon>
                        <CourseIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography fontWeight={700}>{g.name}</Typography>
                            {g.course_code && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={g.course_code}
                                sx={{ fontFamily: 'monospace' }}
                              />
                            )}
                            <Chip size="small" label={g.delivery_type || '—'} />
                          </Stack>
                        }
                        secondary={
                          <>
                            {g.learningSpot?.category?.name || '—'}
                            {g.learningSpot?.name ? ` / ${g.learningSpot.name}` : ''}
                            {' · '}
                            {g.versionCount} version{g.versionCount === 1 ? '' : 's'}
                            {g.hasVideoCall ? ' · has Join Video Call' : ''}
                          </>
                        }
                      />
                      <ChevronRightIcon color="action" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {selectedGroup && !selectedEdition && (
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 2 }}
              gap={1}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedGroup.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a version to open the course outline and recordings.
                </Typography>
              </Box>
              <Button size="small" startIcon={<BackIcon />} onClick={() => setSelectedGroupId(null)}>
                All courses
              </Button>
            </Stack>
            <Stack spacing={1.25}>
              {(selectedGroup.editions || []).map((ed) => (
                <Card key={ed.id} variant="outlined" sx={{ borderColor: 'divider' }}>
                  <CardActionArea onClick={() => setSelectedEditionId(ed.id)} sx={{ p: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <VersionIcon color="primary" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={700}>{ed.edition_label}</Typography>
                          {ed.course_code && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={ed.course_code}
                              sx={{ fontFamily: 'monospace' }}
                            />
                          )}
                          <Chip
                            size="small"
                            label={
                              ed.status === 'admin_blocked' ? 'Admin Blocked' : ed.status || '—'
                            }
                            color={
                              ed.status === 'published'
                                ? 'success'
                                : ed.status === 'admin_blocked'
                                  ? 'error'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                          {ed.canLaunchToday && (
                            <Chip size="small" color="success" label="In session window" />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                          {[ed.start_date, ed.end_date].filter(Boolean).join(' → ') || 'No dates'}
                          {' · '}
                          {(ed.outline || []).length} outline step
                          {(ed.outline || []).length === 1 ? '' : 's'}
                          {ed.hasVideoCall
                            ? ` · ${ed.liveVideoCallItems?.length || 0} Join Video Call`
                            : ' · no Join Video Call'}
                        </Typography>
                      </Box>
                      <ChevronRightIcon color="action" />
                    </Stack>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {selectedGroup && selectedEdition && (
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 1 }}
              gap={1}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedGroup.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedEdition.edition_label}
                    color="primary"
                    variant="outlined"
                  />
                  {selectedEdition.course_code && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={selectedEdition.course_code}
                      sx={{ fontFamily: 'monospace' }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {launchExample ? (
                    <>
                      Launch / recording labels look like:{' '}
                      <Box component="span" sx={{ fontFamily: 'monospace' }}>
                        {launchExample}
                      </Box>{' '}
                      (increments after End → Launch). File recording follows Training Administration
                      → Jitsi Settings (moderator only).
                    </>
                  ) : (
                    <>
                      This edition has no Course ID yet — it will be allocated automatically on the
                      next Launch (format{' '}
                      <Box component="span" sx={{ fontFamily: 'monospace' }}>
                        ABCDEF-SPOT12-1234-5678-V0001-0001
                      </Box>
                      ).
                    </>
                  )}
                </Typography>
              </Box>
              <Button size="small" startIcon={<BackIcon />} onClick={() => setSelectedEditionId(null)}>
                Versions
              </Button>
            </Stack>

            <Tabs value={editionTab} onChange={(_, v) => setEditionTab(v)} sx={{ mb: 2 }}>
              <Tab icon={<OutlineIcon />} iconPosition="start" label="Course outline" />
              <Tab
                icon={<RecordingsIcon />}
                iconPosition="start"
                label={`Recordings (${recordings.length})`}
              />
            </Tabs>

            {selectedEdition.status === 'admin_blocked' && editionTab === 0 && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() =>
                      navigate('/helpdesk/new', {
                        state: {
                          ticketDraft: {
                            subject: `Unblock training: ${selectedGroup.name} (${selectedEdition.edition_label})`,
                            description: `Please unblock course ${selectedGroup.name} edition ${selectedEdition.edition_label} (id: ${selectedEdition.id}).`,
                          },
                        },
                      })
                    }
                  >
                    Log ticket
                  </Button>
                }
              >
                This edition is admin-blocked — launching video is disabled.
              </Alert>
            )}

            {editionTab === 0 &&
              ((selectedEdition.outline || []).length === 0 ? (
                <Typography color="text.secondary">
                  This edition has an empty outline. Add items in Manage Training → Course outline.
                </Typography>
              ) : (
                <List disablePadding>
                  {selectedEdition.outline.map((item) => {
                    const meta = TYPE_META[item.item_type] || {
                      label: item.item_type,
                      icon: OutlineIcon,
                      color: 'default',
                    };
                    const Icon = meta.icon;
                    const liveSession = liveForEditionItem(selectedEdition.id, item.id);
                    const key = `${selectedEdition.id}:${item.id}`;
                    const isVideo = item.item_type === 'live_video_call';
                    return (
                      <ListItem
                        key={item.id}
                        divider
                        alignItems="flex-start"
                        secondaryAction={
                          isVideo ? (
                            <Button
                              size="small"
                              variant="contained"
                              color={liveSession ? 'success' : 'primary'}
                              startIcon={liveSession ? <RejoinIcon /> : <LaunchIcon />}
                              disabled={
                                selectedEdition.status === 'admin_blocked' || launchingKey === key
                              }
                              onClick={() => launchItem(selectedEdition, item)}
                            >
                              {launchingKey === key
                                ? 'Launching…'
                                : liveSession
                                  ? 'Rejoin video'
                                  : 'Launch video'}
                            </Button>
                          ) : null
                        }
                        sx={{ pr: isVideo ? 18 : 2 }}
                      >
                        <ListItemIcon sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            label={item.sequence_number}
                            sx={{ fontWeight: 800, minWidth: 36 }}
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ mt: 1.1, minWidth: 36 }}>
                          <Icon color={isVideo ? 'secondary' : 'action'} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography fontWeight={700}>{outlineItemTitle(item)}</Typography>
                              <Chip
                                size="small"
                                label={meta.label}
                                color={meta.color}
                                variant="outlined"
                              />
                              {liveSession && <Chip size="small" color="success" label="Live now" />}
                            </Stack>
                          }
                          secondary={outlineItemSecondary(item)}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ))}

            {editionTab === 1 && (
              <Box>
                {recordingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : recordingsBySequence.length === 0 ? (
                  <Typography color="text.secondary">
                    No recordings yet for this version. As moderator, use Start recording in Meet;
                    files land under uploads PVC and appear here by label ({launchExample}, …).
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {recordingsBySequence.map((group) => (
                      <Box key={group.label}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                          <Chip
                            size="small"
                            label={group.label}
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {group.items.length} video{group.items.length === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                        <List dense disablePadding>
                          {group.items.map((rec) => {
                            const started = rec.recording_started_at || rec.created_at;
                            const ended = rec.recording_ended_at;
                            const isDeleted = Boolean(rec.marked_for_deletion_at);
                            return (
                              <ListItem
                                key={rec.id}
                                divider
                                alignItems="flex-start"
                                secondaryAction={
                                  <Stack spacing={0.5} alignItems="flex-end">
                                    <Button
                                      size="small"
                                      startIcon={<LaunchIcon />}
                                      onClick={() => setPlayRecording(rec)}
                                    >
                                      Play
                                    </Button>
                                    <Button size="small" onClick={() => openVisibilityDialog(rec)}>
                                      Visibility
                                    </Button>
                                    {isDeleted ? (
                                      <Button size="small" onClick={() => clearDeletionMark(rec)}>
                                        Undo delete
                                      </Button>
                                    ) : (
                                      <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => markForDeletion(rec)}
                                      >
                                        Mark delete
                                      </Button>
                                    )}
                                  </Stack>
                                }
                                sx={{ pr: 18 }}
                              >
                                <ListItemIcon sx={{ mt: 1 }}>
                                  <RecordingsIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                      <Typography variant="body2" fontWeight={700} noWrap>
                                        {rec.file_name}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        icon={
                                          rec.visibility === 'public' ? (
                                            <PublicIcon />
                                          ) : rec.visibility === 'disabled' ? (
                                            <DisabledIcon />
                                          ) : (
                                            <PrivateIcon />
                                          )
                                        }
                                        label={visibilityLabel(rec.visibility)}
                                        color={
                                          rec.visibility === 'public'
                                            ? 'success'
                                            : rec.visibility === 'disabled'
                                              ? 'default'
                                              : 'primary'
                                        }
                                        variant="outlined"
                                      />
                                      {isDeleted && (
                                        <Chip size="small" color="error" label="Marked for deletion" />
                                      )}
                                    </Stack>
                                  }
                                  secondary={
                                    <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                      <Typography variant="caption" component="span" display="block">
                                        Date: {formatDateTime(started)}
                                      </Typography>
                                      <Typography variant="caption" component="span" display="block">
                                        Start: {formatDateTime(started)} · End: {formatDateTime(ended)} ·
                                        Duration: {formatHms(rec.duration_seconds)}
                                      </Typography>
                                      <Typography variant="caption" component="span" display="block">
                                        Available until: {formatDateTime(rec.available_until)}
                                        {formatBytes(rec.byte_size) ? ` · ${formatBytes(rec.byte_size)}` : ''}
                                      </Typography>
                                      {rec.visibility === 'public' && rec.public_page_path && (
                                        <Typography variant="caption" component="span" display="block">
                                          Public link: {window.location.origin}
                                          {rec.public_page_path}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    ))}
                  </Stack>
                )}
                <Button
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => loadRecordings(selectedEdition.id)}
                  disabled={recordingsLoading}
                >
                  Refresh
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
        </>
      )}

      <Dialog
        open={Boolean(playRecording)}
        onClose={() => {
          setPlayRecording(null);
          setPlayUrl(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{playRecording?.recording_label || 'Recording'}</DialogTitle>
        <DialogContent>
          {playRecording && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Start {formatDateTime(playRecording.recording_started_at || playRecording.created_at)} · End{' '}
              {formatDateTime(playRecording.recording_ended_at)} · Duration{' '}
              {formatHms(playRecording.duration_seconds)}
            </Typography>
          )}
          {playUrl ? (
            <Box
              component="video"
              src={playUrl}
              controls
              autoPlay
              sx={{ width: '100%', maxHeight: '80vh', bgcolor: '#000' }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(visibilityDialog)}
        onClose={() => setVisibilityDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Recording visibility</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Public: anyone with the link (no login). Private: enrolled learners. Disabled: instructors
            only. For public/private, set how long the video stays available (default +30 days).
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="rec-visibility-label">Visibility</InputLabel>
            <Select
              labelId="rec-visibility-label"
              label="Visibility"
              value={visibilityForm.visibility}
              onChange={(e) =>
                setVisibilityForm((prev) => ({
                  ...prev,
                  visibility: e.target.value,
                  available_until:
                    e.target.value === 'public' || e.target.value === 'private'
                      ? prev.available_until || defaultAvailableUntilDateInput(30)
                      : prev.available_until,
                }))
              }
            >
              <MenuItem value="private">Private (enrolled students)</MenuItem>
              <MenuItem value="public">Public (no login)</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
          {(visibilityForm.visibility === 'public' || visibilityForm.visibility === 'private') && (
            <TextField
              fullWidth
              type="date"
              label="Available until"
              InputLabelProps={{ shrink: true }}
              value={visibilityForm.available_until || ''}
              onChange={(e) =>
                setVisibilityForm((prev) => ({ ...prev, available_until: e.target.value }))
              }
              helperText="Defaults to +30 days from today when you switch to public/private."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisibilityDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveVisibility} disabled={savingVisibility}>
            {savingVisibility ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
